/* eslint-disable react/jsx-no-bind */
import React, { useContext } from "react";
import { AppBar, Tab, Tabs } from "@material-ui/core";
import CreateIcon from "@material-ui/icons/Create";
import { FormikProps } from "formik";
import { useHistory } from "react-router-dom";
import { a11yProps, TabPanel } from "./components/TabPanel";
import useStyles from "./LaboratoryPageStyles";
import useEnv from "../../hooks/env";
import LaboratoriesApi from "../../api/services/LaboratoriesApi";
import ErrorSnackbarContext from "../../contexts/ErrorSnackbarContext";
import GenericPermissionContext from "../../contexts/GenericPermissionContext";
import ConfirmationDialogContext from "../../components/Dialog/ConfirmationDialog/ConfirmationDialogContext";
import LaboratoryNameRequest from "../../api/models/test-request/LaboratoryNameRequest";
import ManagedLaboratoryResponse from "../../api/models/test-request/ManagedLaboratoryResponse";
import LaboratoryPanel from "./components/LaboratoryPanel/LaboratoryPanel";
import { NavigationContext } from "../../contexts/NavigationContext";
import CustomProgress from "../../components/Alert/CustomProgress";
import useManagedLaboratories from "./hooks/useManagedLaboratories.hook";
import LabNotFound from "./components/LabNotFound/LabNotFound";
import confirmationDialogConfig from "./constants/ConfirmationDialogConfig";
import LaboratoryDialog from "./components/LaboratoryDialog/LaboratoryDialog";

interface LaboratoryPageProps {}

const LaboratoryPage: React.FC<LaboratoryPageProps> = () => {
  const { api } = useEnv();
  const labsApi = new LaboratoriesApi(api);

  const { handleHttpError, showSnackbar } = useContext(ErrorSnackbarContext);
  const { editLaboratoryName } = useContext(GenericPermissionContext);
  const { showConfirmationDialog } = useContext(ConfirmationDialogContext);
  const { dirtyFlag, updateDirtyFlag } = useContext(NavigationContext);

  const history = useHistory();

  const [tabEdited, setTabEdited] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState(0);

  const [activeLaboratory, setActiveLaboratory] = React.useState<ManagedLaboratoryResponse>();
  const [isNameDialogOpen, setIsNameDialogOpen] = React.useState(false);

  // Reference to the underline formik props of the active tab
  const formRef = React.useRef<FormikProps<{ users: { id: number; name: string }[] }>>(null);

  const classes = useStyles();

  const { loading, managedLaboratories } = useManagedLaboratories(tabEdited, setTabEdited, activeTab);

  const handleChange = (event: React.ChangeEvent<unknown>, currentActiveTab: number) => {
    if (dirtyFlag) {
      // the underlining formik form has changed
      showConfirmationDialog(confirmationDialogConfig).then((data) => {
        if (formRef && formRef.current) {
          setTabEdited(true);
          if (data === "DEFAULT") {
            formRef.current?.submitForm();
          } else {
            formRef.current.setFieldValue("users", formRef.current.initialValues.users);
            updateDirtyFlag(false);
          }
        }
      });
    } else {
      history.push(`/admin/labs/${currentActiveTab}`);
      setActiveTab(currentActiveTab);
    }
  };

  const tabTemplate = (index: number, laboratory: ManagedLaboratoryResponse) => {
    return (
      <Tab
        key={laboratory.id}
        className={classes.tab}
        label={laboratory.name}
        icon={
          <>
            {editLaboratoryName && (
              <CreateIcon
                className={classes.tabIcon}
                onClick={() => {
                  setActiveLaboratory(laboratory);
                  setIsNameDialogOpen(true);
                }}
              />
            )}
          </>
        }
        {...a11yProps(index)}
      />
    );
  };

  const tabPanelTemplate = (index: number, laboratory: ManagedLaboratoryResponse) => {
    return (
      <div key={laboratory.key} className={classes.tabPanelContainer}>
        <TabPanel index={index} value={activeTab}>
          <LaboratoryPanel
            laboratory={laboratory}
            formRef={formRef}
            tabEdited={tabEdited}
            handleTabEdited={setTabEdited}
          />
        </TabPanel>
      </div>
    );
  };

  const editLaboratory = (id: number | undefined, data: LaboratoryNameRequest) => {
    if (!id) return;
    labsApi
      .editLaboratoryName(id, data)
      .then(() => showSnackbar("Laboratory data updated", "success"))
      .then(() => setTabEdited(true))
      .catch(handleHttpError);
  };

  if (loading) {
    return <CustomProgress />;
  }
  return managedLaboratories.length > 0 ? (
    <div className={classes.root}>
      {activeLaboratory && (
        <LaboratoryDialog
          open={isNameDialogOpen}
          laboratory={activeLaboratory}
          onClose={() => setIsNameDialogOpen(false)}
          onSave={(data) => {
            editLaboratory(activeLaboratory.id, { id: activeLaboratory.id, ...data } as LaboratoryNameRequest);
            setIsNameDialogOpen(false);
          }}
        />
      )}
      <h2>Administration Area</h2>
      <AppBar position="static">
        <Tabs
          className={classes.paddingInset}
          value={activeTab}
          onChange={handleChange}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="laboratories"
        >
          {managedLaboratories.map((lab, i) => tabTemplate(i, lab))}
        </Tabs>
      </AppBar>
      {managedLaboratories.map((lab, i) => tabPanelTemplate(i, lab))}
    </div>
  ) : (
    <LabNotFound />
  );
};

export default LaboratoryPage;
